<template>
  <div :class="[colorCss, sizeCss]" class="inline-flex items-center ring-1 ring-inset">
    {{ customText }}
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  text: { type: String, required: false, default: '' },
  adjust: { type: Boolean, required: false, default: false },
  color_identifier: { type: String, required: false, default: 'gray' },
  size: { type: String, required: false, default: 'md' },
});

const sizeCss = computed(() => {
  if (props.size == 'sm') return 'px-1.5 py-0.5 text-xs font-medium rounded-md';
  if (props.size == 'md') return 'px-2 py-1 text-xs font-medium rounded-md';
  if (props.size == 'lg') return 'px-3 py-1.5 text-base font-medium rounded-md';
  return 'px-2 py-1 text-xs font-medium rounded-md';
});

const customText = computed(() => {
  if (props.adjust) return getAdjustedText(props.text);
  return props.text;
});

const getAdjustedText = (word) => {
  if (!word) return '';
  // Investor Types
  else if (word == 'partner_admin') return 'Admin';
  else if (word == 'super_admin') return 'Super Admin';
  // Payment Types
  else if (word == 'ach') return 'ACH';
  else if (word == 'creditcard') return 'Credit Card';
  else if (word == 'tbd') return 'TBD';
  // Partner Types
  else if (word == 'dib') return 'DIB';
  else if (word == 'dab') return 'DAB';
  else if (word == 'maas') return 'MAAS';
  // Sigining statuses
  else if (word == 'NOTSIGNED') return 'Not Signed';
  else if (word == 'SIGNED') return 'Signed';
  // Accreditation
  else if (word == 'Verified Accredited') return 'Accredited';
  // Trade statuses
  else if (word == 'refund_pending') return 'Refund Pending'
  // tbas
  else if (word === '/dashboard/settings') return 'Settings Tab';
  else if (word === '/dashboard/investing') return 'Invest Tab';
  else if (word === '/dashboard/documents') return 'Documents Tab';
  else if (word === '/dashboard/education') return 'Education Tab';
  else if (word === '/dashboard/holdings') return 'Holdings Tab';
  else if (word === '/dashboard/payment-methods') return 'Payment Methods Tab';
  else if (word === '/dashboard/accounts') return 'Accounts Tab';
  // Account Types
  else if (word === 'ira') return 'IRA';

  // Capitalize first letter of each word
  const words = word.split(' ');
  const capitalizedWords = words.map(w => w.charAt(0).toUpperCase() + w.slice(1));
  return capitalizedWords.join(' ');
};

const colorCss = computed(() => {
  switch (props.color_identifier) {
    case 'gray':
    case 'archived':
      return 'bg-gray-100 text-gray-600 ring-gray-500/10 dark:bg-gray-400/10 dark:ring-gray-400/20 dark:text-gray-400';
    case 'red':
    case 'Not Accredited':
    case 'failed':
    case 'canceled':
    case 'disapproved':
    case 'Not Invested':
    case 'ROTH IRA':
    case 'roth ira':
    case 'roth':
    case 'sell':
    case 'super_admin':
    case 'voided':
      return 'bg-red-50 text-red-700 ring-red-600/10 dark:bg-red-400/10 dark:ring-red-400/20 dark:text-red-400';
    case 'yellow':
    case 'Pending':
    case 'tbd':
    case 'NOTSIGNED':
    case 'unemployed':
      return 'bg-yellow-50 text-yellow-800 ring-yellow-600/10 dark:bg-yellow-400/10 dark:ring-yellow-400/20 dark:text-yellow-400';
    case 'orange':
    case 'Action Required':
    case 'withdraw':
    case 'declined':
      return 'bg-orange-50 text-orange-700 ring-orange-600/10 dark:bg-orange-400/10 dark:ring-orange-400/20 dark:text-orange-400';
    case 'blue':
    case 'Under Review':
    case 'recieved':
    case 'Invested':
    case 'equity':
    case 'Equity':
    case 'buy':
    case 'deposit':
    case 'retired':
    case 'ach':
    case 'Individual':
    case 'individual':
      return 'bg-blue-50 text-blue-700 ring-blue-600/10 dark:bg-blue-700/10 dark:ring-blue-400/30 dark:text-blue-400';
    case 'green':
    case 'Verified Accredited':
    case 'Accredited':
    case 'Signed':
    case 'settled':
    case 'distributed':
    case 'approved':
    case 'fund':
    case 'joint':
    case 'partner_admin':
    case 'employed':
      return 'bg-green-50 text-green-700 ring-green-600/10 dark:bg-green-500/10 dark:ring-green-500/20 dark:text-green-400';
    case 'teal':
    case 'check':
      return 'bg-teal-50 text-teal-700 ring-teal-600/10 dark:bg-teal-500/10 dark:ring-teal-500/20 dark:text-teal-400';
    case 'indigo':
    case 'custody':
      return 'bg-indigo-50 text-indigo-700 ring-indigo-700/10 dark:bg-indigo-400/10 dark:ring-indigo-400/30 dark:text-indigo-400';
    case 'purple':
    case 'refunded':
    case 'debt':
    case 'Debt':
    case 'advisor':
      return 'bg-purple-50 text-purple-700 ring-purple-700/10 dark:bg-purple-400/10 dark:ring-purple-400/30 dark:text-purple-400';
    case 'pink':
    case 'refund_pending':
    case 'hybrid':
    case 'Hybrid':
    case 'SEP IRA':
    case 'sep ira':
    case 'sep':
    case 'investor':
      return 'bg-pink-50 text-pink-700 ring-pink-700/10 dark:bg-pink-400/10 dark:ring-pink-400/30 dark:text-pink-400';
    default:
      return 'bg-gray-100 text-gray-600 ring-gray-500/10 dark:bg-gray-400/10 dark:ring-gray-400/20 dark:text-gray-400';
  }
});
</script>